import { useCallback } from 'react';

/**
 * Simple toast implementation since we're moving away from notistack
 */
const createToast = (message, type = 'info') => {
    // Robust message handling to prevent "[object Object]" errors
    let displayMessage;
    
    if (message === null || message === undefined) {
        displayMessage = 'No message provided';
    } else if (typeof message === 'string') {
        displayMessage = message;
    } else if (typeof message === 'number' || typeof message === 'boolean') {
        displayMessage = String(message);
    } else if (message instanceof Error) {
        displayMessage = message.message || 'An error occurred';
    } else if (typeof message === 'object') {
        // Handle objects by extracting meaningful information
        if (message.message) {
            displayMessage = String(message.message);
        } else if (message.error) {
            displayMessage = String(message.error);
        } else if (message.data) {
            displayMessage = String(message.data);
        } else {
            // Try to stringify the object nicely
            try {
                displayMessage = JSON.stringify(message);
                // If JSON is too long, truncate it
                if (displayMessage.length > 200) {
                    displayMessage = displayMessage.substring(0, 200) + '...';
                }
            } catch (e) {
                displayMessage = 'Error occurred (could not parse message)';
            }
        }
    } else {
        // Fallback for any other type
        displayMessage = String(message);
    }

    // Ensure we have a valid string
    if (!displayMessage || displayMessage.trim() === '') {
        displayMessage = 'Unknown error occurred';
    }

    // Create toast container if it doesn't exist
    let toastContainer = document.getElementById('toast-container');
    if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.id = 'toast-container';
        toastContainer.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 9999;
            pointer-events: none;
        `;
        document.body.appendChild(toastContainer);
    }

    // Create toast element
    const toast = document.createElement('div');
    const colors = {
        success: '#4caf50',
        error: '#f44336',
        warning: '#ff9800',
        info: '#2196f3'
    };

    toast.style.cssText = `
        background-color: ${colors[type] || colors.info};
        color: white;
        padding: 12px 24px;
        margin-bottom: 10px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        pointer-events: auto;
        opacity: 0;
        transform: translateX(100%);
        transition: all 0.3s ease;
        max-width: 400px;
        word-wrap: break-word;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        font-size: 14px;
        line-height: 1.4;
        cursor: pointer;
    `;
    
    toast.textContent = displayMessage;
    toastContainer.appendChild(toast);

    // Animate in
    setTimeout(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateX(0)';
    }, 10);

    // Auto remove after 5 seconds
    const removeToast = () => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(100%)';
        setTimeout(() => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        }, 300);
    };

    const autoRemoveTimer = setTimeout(removeToast, 5000);

    // Click to dismiss
    toast.addEventListener('click', () => {
        clearTimeout(autoRemoveTimer);
        removeToast();
    });
};

/**
 * Custom hook for standardized API response handling with toast notifications
 * Provides consistent error and success message handling across all pages
 */
export const useApiHandler = () => {
    /**
     * Handle API response with automatic toast notifications
     * @param {Promise} apiCall - The API call promise
     * @param {Object} options - Configuration options
     * @param {string} options.successMessage - Custom success message
     * @param {string} options.errorMessage - Custom error message fallback
     * @param {boolean} options.showSuccess - Whether to show success toast (default: true)
     * @param {boolean} options.showError - Whether to show error toast (default: true)
     * @param {function} options.onSuccess - Callback function on success
     * @param {function} options.onError - Callback function on error
     * @returns {Promise} - Returns the API response or throws error
     */
    const handleApiCall = useCallback(async (apiCall, options = {}) => {
        const {
            successMessage,
            errorMessage = 'Operation failed. Please try again.',
            showSuccess = true,
            showError = true,
            onSuccess,
            onError
        } = options;

        try {
            const response = await apiCall;
            
            // Handle success response
            if (response && response.success !== false) {
                if (showSuccess && successMessage) {
                    createToast(successMessage, 'success');
                }
                
                if (onSuccess) {
                    onSuccess(response);
                }
                
                return response;
            } else {
                // API returned success: false
                const message = response?.message || errorMessage;
                if (showError) {
                    createToast(message, 'error');
                }
                
                if (onError) {
                    onError(response);
                }
                
                throw response;
            }
        } catch (error) {
            // Handle error response
            const message = extractErrorMessage(error, errorMessage);
            
            if (showError) {
                createToast(message, 'error');
            }
            
            if (onError) {
                onError(error);
            }
            
            throw error;
        }
    }, []);

    /**
     * Handle API call with loading state management
     * @param {Promise} apiCall - The API call promise
     * @param {function} setLoading - Loading state setter function
     * @param {Object} options - Same options as handleApiCall
     */
    const handleApiCallWithLoading = useCallback(async (apiCall, setLoading, options = {}) => {
        try {
            setLoading(true);
            return await handleApiCall(apiCall, options);
        } finally {
            setLoading(false);
        }
    }, [handleApiCall]);

    /**
     * Show success message
     * @param {string} message - Success message
     * @param {Object} options - Toast options
     */
    const showSuccess = useCallback((message, options = {}) => {
        createToast(message, 'success');
    }, []);

    /**
     * Show error message
     * @param {string} message - Error message
     * @param {Object} options - Toast options
     */
    const showError = useCallback((message, options = {}) => {
        createToast(message, 'error');
    }, []);

    /**
     * Show info message
     * @param {string} message - Info message
     * @param {Object} options - Toast options
     */
    const showInfo = useCallback((message, options = {}) => {
        createToast(message, 'info');
    }, []);

    /**
     * Show warning message
     * @param {string} message - Warning message
     * @param {Object} options - Toast options
     */
    const showWarning = useCallback((message, options = {}) => {
        createToast(message, 'warning');
    }, []);

    return {
        handleApiCall,
        handleApiCallWithLoading,
        showSuccess,
        showError,
        showInfo,
        showWarning
    };
};

/**
 * Extract error message from various error response formats
 * @param {Object} error - Error object
 * @param {string} fallbackMessage - Fallback message if no specific message found
 * @returns {string} - Extracted error message
 */
const extractErrorMessage = (error, fallbackMessage = 'An error occurred') => {
    // Handle null/undefined errors
    if (!error) {
        return fallbackMessage;
    }
    
    // Handle string errors
    if (typeof error === 'string') {
        return error;
    }

    // Check for API response message
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }
    
    // Check for direct message property
    if (error?.message && typeof error.message === 'string') {
        return error.message;
    }
    
    // Check for Laravel validation errors
    if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        const firstField = Object.keys(errors)[0];
        if (firstField && errors[firstField] && errors[firstField][0]) {
            return errors[firstField][0];
        }
    }
    
    // Check for generic error response
    if (error?.response?.data) {
        return typeof error.response.data === 'string' 
            ? error.response.data 
            : fallbackMessage;
    }
    
    // Network or other errors
    if (error?.code === 'NETWORK_ERROR') {
        return 'Network error. Please check your internet connection.';
    }

    // Handle axios errors
    if (error?.response?.status) {
        switch (error.response.status) {
            case 401:
                return 'Unauthorized. Please log in again.';
            case 403:
                return 'Access denied. You do not have permission to perform this action.';
            case 404:
                return 'Resource not found.';
            case 422:
                return 'Validation error. Please check your input.';
            case 500:
                return 'Internal server error. Please try again later.';
            default:
                return `HTTP Error ${error.response.status}: ${fallbackMessage}`;
        }
    }
    
    // If we get here, the error is likely an object that we can't handle
    console.warn('Unhandled error type:', typeof error, error);
    return fallbackMessage;
};

export default useApiHandler; 